import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import Loader from 'components/Loader';
import { format, parseISO } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useCurrentEmployee } from 'pages/employees/useCurrentEmployee';
import { useMemo } from 'react';
import { useGetTimecardByIdQuery } from 'store/api/timecards';
import { TimecardDto } from 'types/timecards';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import TimecardPageForm from './TimecardPageForm';
import { timecardSecondsToHours, toValueFactorOptions } from './utils/timecardUtils';

const USED_CLASSIFIERS = ['TIMECARD_ACTIVITY_TYPE', 'TIMECARD_VALUE_FACTORS'];

const DuplicateTimecardPage = (): JSX.Element => {
    // In the create dialog the id is the dialog's, where the router's is that of the page behind it.
    const { id: timecardId } = useCreateRoute().params;
    const { data: currentEmployee } = useCurrentEmployee();
    const { data: timecard, isError } = useGetTimecardByIdQuery(parseInt(timecardId || '0'), {
        skip: timecardId === undefined,
    });
    const { isTwoDecimalEntryEnabled } = useTimecardPrecision();
    const dateFormat = useEffectiveDateFormat();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });

    const initialData = useMemo<Partial<TimecardDto> | undefined>(() => {
        if (isLoadingClassifiers) {
            return undefined;
        }
        if (timecard && currentEmployee) {
            return {
                description: timecard.description,
                timecardActivityType:
                    (
                        getOptionsFromClassifier(classifiers['TIMECARD_ACTIVITY_TYPE']) as {
                            label: string;
                            value: string;
                        }[]
                    ).find((val) => val.value === timecard.timecardActivityTypeCode) ?? null,
                matterAssignment: timecard.matterAssignment
                    ? {
                          id: timecard.matterAssignment.id,
                          name: timecard.matterAssignment.name,
                          isNonBillable: timecard.matterAssignment.isNonBillable,
                          preserveNonBillableHours: timecard.matterAssignment.preserveNonBillableHours,
                      }
                    : null,
                matter: timecard.matter,
                remunerationCalcType: timecard.remunerationCalcTypeCode,
                isNonBillable: timecard.isNonBillable,
                preserveNonBillableHours: timecard.preserveNonBillableHours,
                practiceAreas: timecard.practiceAreas,
                timecardDate: timecard.timecardDate,
                billedTimeInSeconds: timecard.billedTimeInSeconds,
                billedTimeInHours: String(
                    timecardSecondsToHours(timecard.billedTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)
                ),
                totalTimeInSeconds: timecard.totalTimeInSeconds,
                totalTimeInHours: String(
                    timecardSecondsToHours(timecard.totalTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)
                ),
                billingUpliftValueFactors: toValueFactorOptions(timecard.billingUpliftValueFactors, classifiers),
                valueFactorsNotes: timecard.valueFactorsNotes ?? '',
                notes: timecard.notes ?? '',
            };
        }
        return undefined;
    }, [classifiers, isTwoDecimalEntryEnabled, timecard, currentEmployee, isLoadingClassifiers]);

    if (initialData === undefined && !isError) {
        return <Loader />;
    }

    // A timecard has no number to go by: the create dialog's header names the source by its date.
    const copyOf = timecard ? format(parseISO(timecard.timecardDate), dateFormat) : undefined;

    return <TimecardPageForm type="add" initialData={initialData} copyOf={copyOf} />;
};

export default DuplicateTimecardPage;
